import { preselectService } from "@/lib/scroll";
import ProductArt from "@/components/art/ProductArt";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const CATEGORY_ICONS = ["home", "store", "building", "video"];

export default function Security() {
    const { t } = useLang();
    return (
        <section id="cctv" data-testid="cctv-section" className="relative py-24 md:py-32 lg:py-40 bg-black overflow-hidden">
            <div className="absolute inset-0 circuit-bg opacity-40 pointer-events-none" />
            <div
                className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan2/70 to-transparent animate-scanline pointer-events-none"
                aria-hidden="true"
            />
            <div className="absolute top-8 right-8 flex items-center gap-2 font-mono2 text-xs tracking-[0.25em] text-white/50">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-rec" />
                REC
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <Eyebrow>{t.security.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mt-4">
                        {t.security.headingA}<span className="text-cyan2">{t.security.headingB}</span>
                    </h2>
                    <p className="mt-5 max-w-lg text-white/60 text-base md:text-lg leading-relaxed">
                        {t.security.sub}
                    </p>
                    <div className="mt-9 grid grid-cols-2 gap-3 max-w-md">
                        {CATEGORY_ICONS.map((icon, i) => (
                            <div
                                key={icon}
                                data-testid={`cctv-category-${icon}`}
                                className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white/85 hover:border-cyan2/40 transition-colors duration-300"
                            >
                                <Icon name={icon} size={17} className="text-cyan2 shrink-0" />
                                {t.security.cats[i]}
                            </div>
                        ))}
                    </div>
                    <button
                        data-testid="cctv-cta"
                        onClick={() => preselectService("CCTV Camera")}
                        className="mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-4 font-bold text-white hover:shadow-[0_0_45px_rgba(0,207,255,0.4)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        {t.security.cta}
                        <Icon name="arrow" size={17} />
                    </button>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="relative rounded-3xl border border-cyan2/15 bg-navy/40 p-8 overflow-hidden">
                        <div className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-cyan2/50" />
                        <div className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-cyan2/50" />
                        <div className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-cyan2/50" />
                        <div className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-cyan2/50" />
                        <div className="relative h-72 md:h-80">
                            <ProductArt type="cctv" accent="#00CFFF" />
                            <img
                                src="/images/products/cctv-cameras.png"
                                alt="CCTV cameras — Farakka Broadband, Murshidabad"
                                loading="lazy"
                                onError={(e) => e.currentTarget.remove()}
                                className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                            />
                        </div>
                        <div className="mt-4 flex items-center justify-between font-mono2 text-[11px] tracking-[0.2em] uppercase text-cyan2/60">
                            <span>{t.security.cam}</span>
                            <span className="animate-energy">{t.security.scanning}</span>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
